import { formatDistanceToNow } from "date-fns";
import { RefreshCw, Trash2, Users } from "lucide-react";
import { ContentCard } from "@/components/ui/ContentCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  INVITE_POOL_LABEL,
  INVITE_ROLE_LABEL,
  type InviteRole,
  type InviteSeatPool,
  type LicenseSummary,
  type PendingInvite,
} from "@/types/inviteTeammates";

export const LicenseSummaryCard = ({ summary }: { summary: LicenseSummary }) => (
  <ContentCard className="space-y-4 p-5 md:p-6">
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
        <Users className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <h2 className="font-literata text-base font-semibold text-stone-900 md:text-lg">Seats available</h2>
        <p className="mt-1 font-inter text-xs text-stone-600 md:text-sm">
          <span className="font-medium text-stone-800">{summary.scopeLabel}</span>
          <span aria-hidden className="mx-1.5 text-stone-300">
            ·
          </span>
          Counts include pending invites so the pool stays accurate as people accept.
        </p>
      </div>
    </div>

    <div className="grid gap-3 md:grid-cols-2">
      {(["leader", "staff"] as InviteSeatPool[]).map((pool) => (
        <PoolBar key={pool} label={INVITE_POOL_LABEL[pool]} value={summary.pools[pool]} />
      ))}
    </div>
  </ContentCard>
);

const PoolBar = ({
  label,
  value,
}: {
  label: string;
  value: { used: number; total: number };
}) => {
  const remaining = Math.max(0, value.total - value.used);
  const ratio = value.total === 0 ? 0 : Math.min(1, value.used / value.total);
  const isNearCap = ratio >= 0.85 && ratio < 1;
  const isFull = ratio >= 1;

  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-inter text-sm font-medium text-stone-800">{label}</p>
        <p className="font-literata text-sm text-stone-600">
          <span className="font-semibold text-stone-900">{value.used}</span>
          <span className="text-stone-400"> / {value.total}</span>
        </p>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-stone-200">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            isFull ? "bg-rose-500" : isNearCap ? "bg-amber-500" : "bg-emerald-500"
          )}
          style={{ width: `${Math.min(100, ratio * 100)}%` }}
          aria-hidden
        />
      </div>
      <p className="mt-2 font-inter text-xs text-stone-500">
        {isFull ? "All seats in use." : `${remaining} ${remaining === 1 ? "seat" : "seats"} available.`}
      </p>
    </div>
  );
};

interface PendingInvitesCardProps {
  invites: PendingInvite[];
  busyInviteId: string | null;
  onCancel: (invite: PendingInvite) => void;
  onResend: (invite: PendingInvite) => void;
  showSchool: boolean;
}

export const PendingInvitesCard = ({
  invites,
  busyInviteId,
  onCancel,
  onResend,
  showSchool,
}: PendingInvitesCardProps) => {
  const sentInvites = invites.filter((i) => i.status === "pending");

  return (
    <ContentCard className="p-0">
      <div className="border-b border-stone-200 px-5 py-4 md:px-6 md:py-5">
        <h2 className="font-literata text-base font-semibold text-stone-900 md:text-lg">Pending invites</h2>
        <p className="mt-1 font-inter text-xs text-stone-600 md:text-sm">
          {sentInvites.length === 0
            ? "Nobody waiting on a join link right now."
            : `${sentInvites.length} ${sentInvites.length === 1 ? "person" : "people"} waiting to join.`}
        </p>
      </div>

      {sentInvites.length === 0 ? (
        <div className="px-5 py-8 text-center font-inter text-sm text-stone-500 md:px-6">
          Pending invites will appear here.
        </div>
      ) : (
        <ul className="divide-y divide-stone-200">
          {sentInvites.map((invite) => (
            <li key={invite.id} className="px-5 py-4 md:px-6 md:py-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-inter text-sm font-medium text-stone-900 md:text-base">{invite.email}</p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-inter text-xs text-stone-500 md:text-sm">
                    <RolePill role={invite.role} />
                    {showSchool && invite.schoolName && (
                      <>
                        <span aria-hidden className="text-stone-300">
                          ·
                        </span>
                        <span>{invite.schoolName}</span>
                      </>
                    )}
                    <span aria-hidden className="text-stone-300">
                      ·
                    </span>
                    <span>Sent {formatRelative(invite.sentAt)}</span>
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyInviteId === invite.id}
                    onClick={() => onResend(invite)}
                  >
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                    Resend
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyInviteId === invite.id}
                    onClick={() => onCancel(invite)}
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Cancel
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </ContentCard>
  );
};

const RolePill = ({ role }: { role: InviteRole }) => {
  const tone =
    role === "leader"
      ? "bg-amber-100 text-amber-800 ring-amber-200"
      : role === "support_staff"
        ? "bg-sky-100 text-sky-800 ring-sky-200"
        : "bg-emerald-100 text-emerald-800 ring-emerald-200";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        tone
      )}
    >
      {INVITE_ROLE_LABEL[role]}
    </span>
  );
};

const formatRelative = (iso: string): string => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "recently";
  }
};
